export function normalizeIeltsListeningData(apiResponse) {
  // Handle the structure: { status: "success", data: { questions: [...] } }
  // or just { data: { questions: ... } } or just the raw object if it's already unwrapped
  const data = apiResponse?.data ?? apiResponse;
  const questionsRaw = data?.questions ?? [];

  const audioMap = new Map();
  const questionOrderMap = {};
  let globalNumber = 1;

  questionsRaw.forEach((q) => {
    // 1️⃣ Resolve audio part (group by sound_url)
    const soundUrl = q.sound_url || "NO_AUDIO";
    if (!audioMap.has(soundUrl)) {
      audioMap.set(soundUrl, {
        audio_id: `audio_${audioMap.size + 1}`,
        sound_url: q.sound_url || null,
        passage: q.passage || null,
        contexts: new Map(),
      });
    }
    const partEntry = audioMap.get(soundUrl);

    // 2️⃣ Resolve context (group by context text)
    const contextTitle = String(q.context || q.instruction || "General context").trim();

    if (!partEntry.contexts.has(contextTitle)) {
      partEntry.contexts.set(contextTitle, {
        context_id: `context_${partEntry.contexts.size + 1}`,
        context_title: contextTitle,
        sections: new Map(),
      });
    }
    const contextEntry = partEntry.contexts.get(contextTitle);

    // 3️⃣ Resolve instruction (group by instruction text)
    // The API provides instruction as a string. We use it as the title.
    const instructionTitle = q.instruction || "Listen and answer the following";

    if (!contextEntry.sections.has(instructionTitle)) {
      contextEntry.sections.set(instructionTitle, {
        instruksi_id: `instruksi_${contextEntry.sections.size + 1}`, // Frontend expects instruksi_id
        instruksi_title: instructionTitle, // Frontend expects instruksi_title
        questions: [],
      });
    }

    // Map question type if necessary (e.g., fill_in_the_blank -> short_answer)
    // Based on the user's provided JSON, typical mapping might be needed if the frontend expects 'short_answer'
    let questionType = q.question_type;
    if (questionType === "fill_in_the_blank") {
      questionType = "short_answer";
    }

    // 4️⃣ Add question
    contextEntry.sections.get(instructionTitle).questions.push({
      id: q.id,
      question_text: q.question, // Frontend expects question_text
      question_type: questionType,
      options: q.options?.map((opt, index) => ({
        ...opt,
        option_text: opt.text, // Frontend expects option_text
        optionIndex: index + 1, // Frontend expects optionIndex (1-based)
      })),
      // We will assign 'number' globally later
    });
  });

  // 5️⃣ Convert Maps → Arrays & number questions globally
  const parts = Array.from(audioMap.values()).map((part, partIndex) => {
    const contexts = Array.from(part.contexts.values()).map(
      (context, contextIndex) => {
        const sections = Array.from(context.sections.values()).map(
          (section, sectionIndex) => {
            section.questions.sort((a, b) => a.id - b.id);
            const startNumber = globalNumber;

            section.questions = section.questions.map((q, questionIndex) => {
              questionOrderMap[q.id] = {
                number: globalNumber,
                partIndex,
                contextIndex,
                sectionIndex,
                questionIndex,
              };
              return {
                ...q,
                number: globalNumber++,
              };
            });

            return {
              ...section,
              start_number: startNumber,
              end_number: globalNumber - 1,
            };
          },
        );

        const contextQuestions = sections.flatMap((section) => section.questions);

        return {
          ...context,
          sound_url: part.sound_url,
          sections,
          questions: contextQuestions,
          start_number: contextQuestions[0]?.number ?? null,
          end_number:
            contextQuestions[contextQuestions.length - 1]?.number ?? null,
        };
      },
    );

    return {
      audio_id: part.audio_id, // Frontend expects audio_id (or mock did)
      sound_url: part.sound_url,
      passage: part.passage,
      contexts,
    };
  });

  return {
    parts,
    total_questions: globalNumber - 1,
    questionOrderMap,
  };
}
